import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Printer, FileSpreadsheet, FileText, Eye, Filter, RefreshCw,
  Calendar, Users, BookOpen, AlertTriangle, Download, ArrowLeft,
  Hash, Copy, Sun, Search, LayoutList, Grid3X3, Layers, Table2,
  CheckCircle2, Circle, Trash2
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const API_URL = window.location.origin || '';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const TIPO_LABELS = { prova_escrita: 'Prova Escrita', trabalho: 'Trabalho', producao_sala: 'Produção em Sala', seminario: 'Seminário', outro: 'Outro' };

// V4.15: Timeline compacta (Aprovada → Enviada → Impressa) para itens vindos do Editor
const _fmtDtBR = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch (_) { return ''; }
};
const AssessmentTimeline = ({ item, compact = true }) => {
  if (!item?.editor_assessment_id) return null;
  const sentAt = item.editor_assessment_sent_at || null;
  const printedAt = item.impresso_em || null;
  const done = { approved: !!sentAt, sent: !!sentAt, printed: !!printedAt };
  const steps = [
    { key: 'approved', label: 'Aprovada', ts: sentAt, active: done.approved, color: '#4f46e5' },
    { key: 'sent',     label: 'Enviada',  ts: sentAt, active: done.sent,     color: '#0e7490' },
    { key: 'printed',  label: 'Impressa', ts: printedAt, active: done.printed, color: '#15803d' },
  ];
  return (
    <div
      data-testid={`timeline-${item.id}`}
      className={`flex items-center gap-1 ${compact ? 'mt-1' : 'mt-2'}`}
      title={steps.filter(s => s.active).map(s => `${s.label}${s.ts ? ' · ' + _fmtDtBR(s.ts) : ''}`).join(' → ')}
    >
      {steps.map((s, idx) => (
        <React.Fragment key={s.key}>
          <span
            className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5"
            style={{
              background: s.active ? `${s.color}15` : '#f1f5f9',
              color: s.active ? s.color : '#94a3b8',
              border: `1px solid ${s.active ? s.color + '55' : '#e2e8f0'}`,
              fontSize: 9, fontWeight: 600, whiteSpace: 'nowrap',
            }}
          >
            <span
              className="inline-block rounded-full"
              style={{ width: 6, height: 6, background: s.active ? s.color : '#cbd5e1' }}
            />
            {s.label}
          </span>
          {idx < steps.length - 1 && (
            <span
              aria-hidden="true"
              className="inline-block"
              style={{
                width: 8, height: 2,
                background: steps[idx].active && steps[idx + 1].active
                  ? steps[idx + 1].color + '80'
                  : '#e2e8f0',
                borderRadius: 2,
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ─── Small Components ──────────────────────────

const KPI = ({ label, value, icon: Icon, color = '#64327D' }) => (
  <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
    <div className="flex items-center gap-2 mb-1">
      <Icon className="h-4 w-4" style={{ color }} />
      <span className="text-xs text-slate-500">{label}</span>
    </div>
    <p className="text-2xl font-bold text-slate-800">{value}</p>
  </div>
);

const AlertBadge = ({ item }) => {
  const alerts = [];
  if (item.hasMissingTeacher) alerts.push('Sem professor');
  if (item.hasMissingStudentCount) alerts.push('Sem alunos');
  if (item.hasScheduleConflict) alerts.push('Conflito');
  if (alerts.length === 0) return null;
  return <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">{alerts.join(' | ')}</span>;
};

const TypeBadge = ({ type }) => (
  <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 font-medium">{TIPO_LABELS[type] || type}</span>
);

// ─── Main Page ──────────────────────────

export default function AssessmentPrintReportPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPlanId = searchParams.get('planId') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [planos, setPlanos] = useState([]);
  const [viewMode, setViewMode] = useState('consolidated');
  const [secretaryMode, setSecretaryMode] = useState(false);

  // Filters
  const [planId, setPlanId] = useState(initialPlanId);
  const [trimestre, setTrimestre] = useState('');
  const [turmaId, setTurmaId] = useState('');
  const [search, setSearch] = useState('');
  const [extraCopies, setExtraCopies] = useState(2);
  const [printFilter, setPrintFilter] = useState(''); // '' | 'impresso' | 'nao_impresso'

  // Load plans
  useEffect(() => {
    fetch(`${API_URL}/api/assessment-week/planos`, { headers: headers() })
      .then(r => r.json()).then(d => setPlanos(d.planos || [])).catch(() => {});
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (planId) params.set('plan_id', planId);
    if (trimestre) params.set('trimestre', trimestre);
    if (turmaId) params.set('turma_id', turmaId);
    if (search) params.set('search', search);
    params.set('view_mode', viewMode);
    params.set('print_extra_copies', String(extraCopies));

    try {
      const res = await fetch(`${API_URL}/api/assessment-week/relatorio-impressao?${params}`, { headers: headers() });
      const d = await res.json();
      setData(d);
    } catch { toast.error('Erro ao carregar relatório'); }
    finally { setLoading(false); }
  }, [planId, trimestre, turmaId, search, viewMode, extraCopies]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // Persist filters
  useEffect(() => {
    try { localStorage.setItem('assessmentPrintFilters', JSON.stringify({ planId, trimestre, turmaId, extraCopies })); } catch {}
  }, [planId, trimestre, turmaId, extraCopies]);

  const summary = data?.summary || {};
  const rawItems = data?.items || [];
  const grouped = data?.grouped || {};

  // Filter by print status
  const items = useMemo(() => {
    if (!printFilter) return rawItems;
    return rawItems.filter(i => printFilter === 'impresso' ? i.impresso : !i.impresso);
  }, [rawItems, printFilter]);

  const toggleImpresso = async (itemId, current) => {
    try {
      await fetch(`${API_URL}/api/assessment-week/relatorio-impressao/marcar-impresso?item_id=${itemId}&impresso=${!current}`, { method: 'POST', headers: headers() });
      fetchReport();
    } catch { toast.error('Erro ao atualizar'); }
  };

  const marcarTodosImpresso = async () => {
    const ids = items.filter(i => !i.impresso).map(i => i.id);
    if (ids.length === 0) return;
    try {
      await fetch(`${API_URL}/api/assessment-week/relatorio-impressao/marcar-impresso-lote`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ item_ids: ids, impresso: true })
      });
      toast.success(`${ids.length} itens marcados como impressos`);
      fetchReport();
    } catch { toast.error('Erro'); }
  };

  // Unique turmas from loaded plans for filter
  const turmaOptions = useMemo(() => {
    const map = {};
    items.forEach(i => { if (i.class_name) map[i.class_name] = i.class_name; });
    return Object.values(map).sort();
  }, [items]);

  const handleExportExcel = () => {
    const params = new URLSearchParams();
    if (planId) params.set('plan_id', planId);
    if (trimestre) params.set('trimestre', trimestre);
    params.set('print_extra_copies', String(extraCopies));
    window.open(`${API_URL}/api/assessment-week/relatorio-impressao/export/excel?${params}`, '_blank');
  };

  const handleExportPDF = () => {
    const params = new URLSearchParams();
    if (planId) params.set('plan_id', planId);
    if (trimestre) params.set('trimestre', trimestre);
    params.set('view_mode', viewMode);
    params.set('print_extra_copies', String(extraCopies));
    window.open(`${API_URL}/api/assessment-week/relatorio-impressao/export/pdf?${params}`, '_blank');
  };

  // ─── Render ──────────────────────────

  return (
    <div className={`min-h-screen ${secretaryMode ? 'bg-white' : 'bg-gray-50'} p-6`} data-testid="assessment-print-report">
      {/* Header */}
      <div className={`flex items-center justify-between mb-6 ${secretaryMode ? 'print:hidden' : ''}`}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/semana-avaliacoes')} className="p-2 rounded-lg hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Printer className="h-5 w-5 text-purple-600" /> Relatório de Impressão de Avaliações
            </h1>
            <p className="text-sm text-gray-500">Controle de cópias para a secretaria</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setSecretaryMode(!secretaryMode)} className={secretaryMode ? 'bg-purple-100 text-purple-700' : ''}>
            <Eye className="h-4 w-4 mr-1" /> {secretaryMode ? 'Modo Normal' : 'Modo Secretaria'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="h-4 w-4 mr-1" /> Imprimir
          </Button>
          <Button variant="outline" size="sm" onClick={marcarTodosImpresso} title="Marcar visíveis como impressos">
            <CheckCircle2 className="h-4 w-4 mr-1" /> Marcar Impressos
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <FileText className="h-4 w-4 mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      {!secretaryMode && (
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm mb-6 print:hidden">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Plano</label>
              <select value={planId} onChange={e => setPlanId(e.target.value)}
                className="w-full h-9 rounded-lg border border-gray-200 px-2 text-sm">
                <option value="">Todos os planos</option>
                {planos.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.trimestre}ºTri {p.ano} {p.turma_names?.join(', ') || ''} ({p.status})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Trimestre</label>
              <select value={trimestre} onChange={e => setTrimestre(e.target.value)}
                className="w-full h-9 rounded-lg border border-gray-200 px-2 text-sm">
                <option value="">Todos</option>
                <option value="1">1º Trimestre</option>
                <option value="2">2º Trimestre</option>
                <option value="3">3º Trimestre</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Turma</label>
              <select value={turmaId} onChange={e => setTurmaId(e.target.value)}
                className="w-full h-9 rounded-lg border border-gray-200 px-2 text-sm">
                <option value="">Todas</option>
                {turmaOptions.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Buscar</label>
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Disciplina, professor..."
                className="h-9 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Cópias extras</label>
              <Input type="number" value={extraCopies} onChange={e => setExtraCopies(Number(e.target.value))}
                min={0} max={10} className="h-9 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Status impressão</label>
              <select value={printFilter} onChange={e => setPrintFilter(e.target.value)}
                className="w-full h-9 rounded-lg border border-gray-200 px-2 text-sm">
                <option value="">Todos</option>
                <option value="nao_impresso">Não impressos</option>
                <option value="impresso">Impressos</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-3 mb-6">
        <KPI label="Avaliações" value={summary.totalAssessments || 0} icon={Hash} />
        <KPI label="Dias" value={summary.totalDays || 0} icon={Calendar} color="#3B82F6" />
        <KPI label="Disciplinas" value={summary.totalSubjects || 0} icon={BookOpen} color="#10B981" />
        <KPI label="Turmas" value={summary.totalClasses || 0} icon={Users} color="#F59E0B" />
        <KPI label="Alunos" value={summary.totalStudents || 0} icon={Users} color="#EC4899" />
        <KPI label="Total Cópias" value={summary.totalPrintQuantity || 0} icon={Copy} color="#EF4444" />
        <KPI label="Dia + Movimentado" value={summary.busiestDay?.date?.substring(5) || '-'} icon={Sun} color="#8E52AB" />
      </div>

      {/* View Mode Tabs */}
      <Tabs value={viewMode} onValueChange={v => setViewMode(v)} className="print:hidden mb-4">
        <TabsList className="bg-gray-100 rounded-xl p-1">
          <TabsTrigger value="consolidated" className="rounded-lg text-xs"><Table2 className="h-3.5 w-3.5 mr-1" /> Consolidado</TabsTrigger>
          <TabsTrigger value="by_day" className="rounded-lg text-xs"><Calendar className="h-3.5 w-3.5 mr-1" /> Por Dia</TabsTrigger>
          <TabsTrigger value="by_subject" className="rounded-lg text-xs"><BookOpen className="h-3.5 w-3.5 mr-1" /> Por Disciplina</TabsTrigger>
          <TabsTrigger value="by_grade" className="rounded-lg text-xs"><Layers className="h-3.5 w-3.5 mr-1" /> Por Série</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Content */}
      {loading ? (
        <div className="animate-pulse space-y-3">
          {[...Array(5)].map((_, i) => <div key={i} className="h-12 rounded-lg bg-gray-100" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <AlertTriangle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Nenhuma avaliação encontrada</p>
          <p className="text-sm text-gray-400 mt-1">Selecione um plano ou ajuste os filtros</p>
        </div>
      ) : viewMode === 'consolidated' ? (
        <ConsolidatedTable items={items} secretaryMode={secretaryMode} toggleImpresso={toggleImpresso} onExamUploaded={fetchReport} onItemDeleted={fetchReport} />
      ) : viewMode === 'by_day' ? (
        <GroupByDay grouped={grouped} secretaryMode={secretaryMode} />
      ) : viewMode === 'by_subject' ? (
        <GroupBySubject grouped={grouped} secretaryMode={secretaryMode} />
      ) : viewMode === 'by_grade' ? (
        <GroupByGrade grouped={grouped} secretaryMode={secretaryMode} />
      ) : null}

      {/* Footer */}
      {items.length > 0 && (
        <div className="mt-4 p-3 bg-purple-50 rounded-xl border border-purple-100 flex items-center justify-between text-sm print:block">
          <span className="text-purple-700 font-medium">
            Total: {summary.totalAssessments} avaliações | {summary.totalPrintQuantity} cópias necessárias
          </span>
          <span className="text-purple-500 text-xs">Cópias = alunos + {extraCopies} extras por avaliação</span>
        </div>
      )}

      {/* Print CSS */}
      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
          body { font-size: 10px; }
          table { font-size: 9px; }
          @page { size: landscape; margin: 8mm; }
        }
      `}</style>
    </div>
  );
}


// ─── Consolidated Table ──────────────────────────

function ConsolidatedTable({ items, secretaryMode, toggleImpresso, onExamUploaded, onItemDeleted }) {
  const fileRefs = React.useRef({});
  const [uploadingId, setUploadingId] = React.useState(null);
  const [deletingId, setDeletingId] = React.useState(null);

  const handleUpload = async (itemId, file) => {
    if (!file) return;
    setUploadingId(itemId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await fetch(`${API_URL}/api/assessment-week/items/${itemId}/exam-file`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, body: formData
      });
      if (onExamUploaded) onExamUploaded();
    } catch { }
    finally { setUploadingId(null); }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Deseja excluir esta avaliacao?')) return;
    setDeletingId(itemId);
    try {
      const res = await fetch(`${API_URL}/api/assessment-week/avaliacoes/${itemId}`, {
        method: 'DELETE', headers: headers()
      });
      if (res.ok) {
        toast.success('Avaliacao excluida');
        if (onItemDeleted) onItemDeleted();
      } else {
        toast.error('Erro ao excluir');
      }
    } catch { toast.error('Erro ao excluir'); }
    finally { setDeletingId(null); }
  };

  const buildExamFileName = (subjectName, className) => {
    const name = `${subjectName || 'Prova'} - ${className || 'Turma'}`;
    return name.replace(/[<>:"/\\|?*]/g, '_');
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-gray-50 border-b">
            <th className="text-center py-2.5 px-2 font-medium text-gray-600 w-10">Status</th>
            <th className="text-left py-2.5 px-3 font-medium text-gray-600">Data</th>
            <th className="text-left py-2.5 px-3 font-medium text-gray-600">Dia</th>
            <th className="text-left py-2.5 px-3 font-medium text-gray-600">Turma</th>
            <th className="text-center py-2.5 px-3 font-medium text-gray-600">Prova</th>
            <th className="text-left py-2.5 px-3 font-medium text-gray-600">Disciplina</th>
            {!secretaryMode && <th className="text-left py-2.5 px-3 font-medium text-gray-600">Professor</th>}
            <th className="text-left py-2.5 px-3 font-medium text-gray-600">Horario</th>
            {!secretaryMode && <th className="text-left py-2.5 px-3 font-medium text-gray-600">Tipo</th>}
            <th className="text-center py-2.5 px-3 font-medium text-gray-600">Alunos</th>
            <th className="text-center py-2.5 px-3 font-medium text-purple-700 bg-purple-50">Imprimir</th>
            {!secretaryMode && <th className="text-center py-2.5 px-3 font-medium text-gray-600">Alertas</th>}
            <th className="text-center py-2.5 px-2 font-medium text-red-600 w-10">Excluir</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i, idx) => (
            <tr key={idx} className={`border-b border-gray-50 hover:bg-gray-50 ${(i.hasMissingStudentCount || i.hasScheduleConflict) ? 'bg-red-50/50' : ''} ${i.impresso ? 'opacity-60' : ''}`}>
              <td className="py-2 px-2 text-center">
                <button onClick={() => toggleImpresso(i.id, i.impresso)} title={i.impresso ? 'Impresso — clique para desmarcar' : 'Nao impresso — clique para marcar'}
                  className="p-0.5 rounded hover:bg-gray-100 transition-colors">
                  {i.impresso
                    ? <CheckCircle2 className="h-4 w-4 text-green-500" />
                    : <Circle className="h-4 w-4 text-gray-300" />}
                </button>
              </td>
              <td className="py-2 px-3 font-medium">{i.assessment_date}</td>
              <td className="py-2 px-3 text-gray-500">{i.weekday?.substring(0, 3)}</td>
              <td className="py-2 px-3 font-medium">{i.class_name}</td>
              <td className="py-1.5 px-2 text-center">
                {uploadingId === i.id ? (
                  <span className="text-[10px] text-gray-400">Enviando...</span>
                ) : i.has_uploaded_exam ? (
                  <>
                  <div className="flex items-center justify-center gap-1">
                    <label className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-200 text-[10px] cursor-pointer">
                      Trocar
                      <input type="file" className="hidden" accept=".pdf,.doc,.docx,.png,.jpg" onChange={e => handleUpload(i.id, e.target.files?.[0])} />
                    </label>
                    <a href={`${API_URL}${i.exam_file_url}?download_name=${encodeURIComponent(buildExamFileName(i.subject_name, i.class_name))}`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 text-[10px] font-medium">
                      <Printer className="h-3 w-3" /> Imprimir
                    </a>
                    {/* V4.14: "Abrir avaliação" (Editor) */}
                    {i.editor_assessment_id && (
                      <a
                        href={`/v2/academico/editor-avaliacoes/${i.editor_assessment_id}`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`open-editor-${i.id}`}
                        title={i.editor_assessment_title || 'Abrir avaliação do editor'}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 text-[10px] font-medium"
                      >
                        <FileText className="h-3 w-3" /> Abrir
                      </a>
                    )}
                  </div>
                  {/* V4.15: Timeline Aprovada → Enviada → Impressa */}
                  <div className="flex justify-center"><AssessmentTimeline item={i} /></div>
                  </>
                ) : (
                  <>
                  <div className="flex items-center justify-center gap-1">
                    <label className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 text-[10px] font-medium cursor-pointer">
                      Upload prova
                      <input type="file" className="hidden" accept=".pdf,.doc,.docx,.png,.jpg" onChange={e => handleUpload(i.id, e.target.files?.[0])} />
                    </label>
                    {/* V4.14: "Abrir avaliação" quando veio do editor (mesmo sem upload de arquivo) */}
                    {i.editor_assessment_id && (
                      <a
                        href={`/v2/academico/editor-avaliacoes/${i.editor_assessment_id}`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`open-editor-${i.id}`}
                        title={i.editor_assessment_title || 'Abrir avaliação do editor'}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 text-[10px] font-medium"
                      >
                        <FileText className="h-3 w-3" /> Abrir avaliação
                      </a>
                    )}
                  </div>
                  {/* V4.15: Timeline Aprovada → Enviada → Impressa */}
                  <div className="flex justify-center"><AssessmentTimeline item={i} /></div>
                  </>
                )}
              </td>
              <td className="py-2 px-3">{i.subject_name}</td>
              {!secretaryMode && <td className="py-2 px-3 text-gray-500">{i.teacher_name || <span className="text-red-400">!</span>}</td>}
              <td className="py-2 px-3 text-gray-500">{i.time} ({i.lesson_slot})</td>
              {!secretaryMode && <td className="py-2 px-3"><TypeBadge type={i.assessment_type} /></td>}
              <td className="py-2 px-3 text-center">{i.student_count}</td>
              <td className="py-2 px-3 text-center font-bold text-purple-700 bg-purple-50/30">{i.print_quantity}</td>
              {!secretaryMode && <td className="py-2 px-3 text-center"><AlertBadge item={i} /></td>}
              <td className="py-2 px-2 text-center">
                <button
                  data-testid={`delete-item-${i.id}`}
                  onClick={() => handleDelete(i.id)}
                  disabled={deletingId === i.id}
                  title="Excluir esta avaliacao"
                  className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-40">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ─── Group by Day ──────────────────────────

function GroupByDay({ grouped, secretaryMode }) {
  const days = Object.values(grouped).sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  return (
    <div className="space-y-4">
      {days.map((day, di) => (
        <div key={di} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
            <span className="font-bold text-blue-800"><Calendar className="h-4 w-4 inline mr-2" />{day.date} — {day.weekday}</span>
            <span className="text-xs text-blue-600">{day.subjects?.length} disciplina(s)</span>
          </div>
          {day.subjects?.map((subj, si) => (
            <div key={si} className="px-4 py-2 border-b border-gray-50">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-800">{subj.subjectName}</span>
                <span className="text-xs text-purple-600">{subj.totalPrintQuantity} cópias</span>
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {subj.items.map((i, ii) => (
                    <tr key={ii} className="border-b border-gray-50/50">
                      <td className="py-1 pr-4 font-medium">{i.class_name}</td>
                      {!secretaryMode && <td className="py-1 pr-4 text-gray-500">{i.teacher_name}</td>}
                      <td className="py-1 pr-4 text-gray-500">{i.time}</td>
                      <td className="py-1 pr-4 text-center">{i.student_count} alunos</td>
                      <td className="py-1 font-bold text-purple-700 text-right">{i.print_quantity} cópias</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}


// ─── Group by Subject ──────────────────────────

function GroupBySubject({ grouped, secretaryMode }) {
  const subjects = Object.values(grouped).sort((a, b) => (a.subjectName || '').localeCompare(b.subjectName || ''));
  return (
    <div className="space-y-4">
      {subjects.map((subj, si) => (
        <div key={si} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-green-50 border-b border-green-100 flex items-center justify-between">
            <span className="font-bold text-green-800"><BookOpen className="h-4 w-4 inline mr-2" />{subj.subjectName}</span>
            <span className="text-xs text-green-600">{subj.totalClasses} turma(s) | {subj.totalPrintQuantity} cópias</span>
          </div>
          <table className="w-full text-xs">
            <thead><tr className="bg-gray-50/50"><th className="text-left py-2 px-3">Data</th><th className="text-left py-2 px-3">Turma</th>{!secretaryMode && <th className="text-left py-2 px-3">Professor</th>}<th className="text-left py-2 px-3">Horário</th><th className="text-center py-2 px-3">Alunos</th><th className="text-center py-2 px-3 text-purple-700">Imprimir</th></tr></thead>
            <tbody>
              {subj.items.map((i, ii) => (
                <tr key={ii} className="border-b border-gray-50"><td className="py-1.5 px-3">{i.assessment_date}</td><td className="py-1.5 px-3 font-medium">{i.class_name}</td>{!secretaryMode && <td className="py-1.5 px-3 text-gray-500">{i.teacher_name}</td>}<td className="py-1.5 px-3 text-gray-500">{i.time}</td><td className="py-1.5 px-3 text-center">{i.student_count}</td><td className="py-1.5 px-3 text-center font-bold text-purple-700">{i.print_quantity}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}


// ─── Group by Grade ──────────────────────────

function GroupByGrade({ grouped, secretaryMode }) {
  const grades = Object.values(grouped).sort((a, b) => (a.gradeName || '').localeCompare(b.gradeName || ''));
  return (
    <div className="space-y-4">
      {grades.map((grade, gi) => (
        <div key={gi} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
            <span className="font-bold text-amber-800"><Layers className="h-4 w-4 inline mr-2" />{grade.gradeName}</span>
          </div>
          {grade.classes?.map((cls, ci) => (
            <div key={ci} className="px-4 py-2 border-b border-gray-50">
              <p className="text-sm font-medium text-gray-700 mb-1">{cls.className}</p>
              <table className="w-full text-xs">
                <tbody>
                  {cls.items.map((i, ii) => (
                    <tr key={ii} className="border-b border-gray-50/50">
                      <td className="py-1 pr-4">{i.assessment_date}</td>
                      <td className="py-1 pr-4">{i.subject_name}</td>
                      {!secretaryMode && <td className="py-1 pr-4 text-gray-500">{i.teacher_name}</td>}
                      <td className="py-1 pr-4 text-gray-500">{i.time}</td>
                      <td className="py-1 text-center">{i.student_count}</td>
                      <td className="py-1 font-bold text-purple-700 text-right">{i.print_quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
